// ----------------------------------------------------------------------
// AdalJS v1.0.15
// @preserve Copyright (c) Microsoft Open Technologies, Inc.
// All Rights Reserved
// Apache License 2.0
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
// id
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ----------------------------------------------------------------------

import { guid } from '../utils/string';

export class AuthenticationContext {
  // Configuration options for Authentication Context.
  private instance: string; // Azure Active Directory Instance.Defaults to `https://login.microsoftonline.com/`.

  private readonly CONSTANTS = {
    ACCESS_TOKEN: 'access_token',
    EXPIRES_IN: 'expires_in',
    ID_TOKEN: 'id_token',
    ERROR_DESCRIPTION: 'error_description',
    SESSION_STATE: 'session_state',
    STORAGE: {
      TOKEN_KEYS: 'adal.token.keys',
      ACCESS_TOKEN_KEY: 'adal.access.token.key',
      EXPIRATION_KEY: 'adal.expiration.key',
      STATE_LOGIN: 'adal.state.login',
      STATE_RENEW: 'adal.state.renew',
      NONCE_IDTOKEN: 'adal.nonce.idtoken',
      SESSION_STATE: 'adal.session.state',
      USERNAME: 'adal.username',
      IDTOKEN: 'adal.idtoken',
      ERROR: 'adal.error',
      ERROR_DESCRIPTION: 'adal.error.description',
      LOGIN_REQUEST: 'adal.login.request',
      LOGIN_ERROR: 'adal.login.error',
      RENEW_STATUS: 'adal.token.renew.status'
    },
    RESOURCE_DELIMETER: '|',
    LOADFRAME_TIMEOUT: 20000,
    TOKEN_RENEW_STATUS_CANCELED: 'Canceled',
    TOKEN_RENEW_STATUS_COMPLETED: 'Completed',
    TOKEN_RENEW_STATUS_IN_PROGRESS: 'In Progress',
    LOGGING_LEVEL: {
      ERROR: 0,
      WARN: 1,
      INFO: 2,
      VERBOSE: 3
    },
    LEVEL_STRING_MAP: {
      0: 'ERROR:',
      1: 'WARNING:',
      2: 'INFO:',
      3: 'VERBOSE:'
    },
    POPUP_WIDTH: 483,
    POPUP_HEIGHT: 600
  };

  /**
   * Creates a new AuthenticationContext object.
   * @constructor
   * @param {config}  config               Configuration options for AuthenticationContext
   */

  private config; // Configuration options for AuthenticationContext
  private callback;
  private isAngular;

  // private
  private user = {};
  private activeRenewals = {};
  private idTokenNonce;
  private profile; // properties parsed from idtoken.

  constructor(config) {
    // public
    this.instance = 'https://login.microsoftonline.com/';
    this.config = {};
    this.callback = null;
    this.isAngular = false;

    // private
    this.user = {};
    this.activeRenewals = {};

    window.renewStates = [];
    window.callbackMappedToRenewStates = {};
    window.callbackMappedToRenewStates = {};

    // validate before constructor assignments
    if (config.displayCall && typeof config.displayCall !== 'function') {
      throw new Error('displayCall is not a function');
    }

    if (!config.clientId) {
      throw new Error('clientId is required');
    }

    this.config = this.cloneConfig(config);

    if (this.config.navigateToLoginRequestUrl === undefined) {
      this.config.navigateToLoginRequestUrl = true;
    }

    if (this.config.callback && typeof this.config.callback === 'function') {
      this.callback = this.config.callback;
    }

    if (this.config.instance) {
      this.instance = this.config.instance;
    }

    // App can request idtoken for itself using clientid as resource
    if (!this.config.loginResource) {
      this.config.loginResource = this.config.clientId;
    }

    // redirect and logout_redirect are set to current location by default
    if (!this.config.redirectUri) {
      // strip off query parameters or hashes from the redirect uri as AAD does not allow those.
      this.config.redirectUri = window.location.href.split('?')[0].split('#')[0];
    }

    if (!this.config.postLogoutRedirectUri) {
      // strip off query parameters or hashes from the post logout redirect uri as AAD does not allow those.
      this.config.postLogoutRedirectUri = window.location.href.split('?')[0].split('#')[0];
    }

    if (!this.config.anonymousEndpoints) {
      this.config.anonymousEndpoints = [];
    }

    if (this.config.isAngular) {
      this.isAngular = this.config.isAngular;
    }

    if (this.config.loadFrameTimeout) {
      this.CONSTANTS.LOADFRAME_TIMEOUT = this.config.loadFrameTimeout;
    }
  }

  /**
   * Acquires token from the cache if it is not expired. Otherwise sends request to AAD to obtain a new token.
   * @param {string}   resource  ResourceUri identifying the target resource
   * @param {tokenCallback} callback -  The callback provided by the caller. It will be called with token or error.
   */
  public acquireToken(resource, callback) {
    if (this.isEmpty(resource)) {
      this.warn('resource is required');
      callback('resource is required', null, 'resource is required');
      return;
    }

    const token = this.getCachedToken(resource);
    if (token) {
      this.info('Token is already in cache for resource:' + resource);
      callback(null, token, null);
      return;
    }

    if (!this.user) {
      this.warn('User login is required');
      callback('User login is required', null, 'login required');
      return;
    }

    // refresh attept with iframe
    // Already renewing for this resource, callback when we get the token.
    if (this.activeRenewals[resource]) {
      // Active renewals contains the state for each renewal.
      this.registerCallback(this.activeRenewals[resource], resource, callback);
    } else {
      if (resource === this.config.clientId) {
        // App uses idtoken to send to api endpoints
        // Default resource is tracked as clientid to store this token
        this.verbose('renewing idtoken');
        this.renewIdToken(callback);
      } else {
        this.renewToken(resource, callback);
      }
    }
  }

  public removeAdalFrame(iframeId) {
    if (typeof iframeId === 'undefined') {
      return;
    }

    this.info('Remove adal frame to document:' + iframeId);
    const adalFrame = document.getElementById(iframeId);

    if (adalFrame) {
      document.body.removeChild(adalFrame);
    }
  }

  // private loginInProgress() {
  //     return this._loginInProgress;
  // };

  /**
   * Checks for the resource in the cache. By default, cache location is Session Storage
   * @ignore
   * @returns {Boolean} 'true' if login is in progress, else returns 'false'.
   */
  private hasResource(key) {
    const keys = this.getItem(this.CONSTANTS.STORAGE.TOKEN_KEYS);
    return keys && !this.isEmpty(keys) && keys.indexOf(key + this.CONSTANTS.RESOURCE_DELIMETER) > -1;
  }

  /**
   * Gets token for the specified resource from the cache.
   * @param {string}   resource A URI that identifies the resource for which the token is requested.
   * @returns {string} token if if it exists and not expired, otherwise null.
   */
  private getCachedToken(resource) {
    if (!this.hasResource(resource)) {
      return null;
    }

    const token = this.getItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY + resource);
    const expiry = this.getItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + resource);

    // If expiration is within offset, it will force renew
    const offset = this.config.expireOffsetSeconds || 300;

    if (expiry && expiry > this.now() + offset) {
      return token;
    } else {
      this.saveItem(this.CONSTANTS.STORAGE.ACCESS_TOKEN_KEY + resource, '');
      this.saveItem(this.CONSTANTS.STORAGE.EXPIRATION_KEY + resource, 0);
      return null;
    }
  }

  /**
   * Adds the passed callback to the array of callbacks for the specified resource and puts the array on the window object.
   * @param {string}   resource A URI that identifies the resource for which the token is requested.
   * @param {string}   expectedState A unique identifier (guid).
   * @param {tokenCallback} callback - The callback provided by the caller. It will be called with token or error.
   */
  private registerCallback(expectedState, resource, callback) {
    this.activeRenewals[resource] = expectedState;
    if (!window.callbackMappedToRenewStates[expectedState]) {
      window.callbackMappedToRenewStates[expectedState] = [];
    }
    window.callbackMappedToRenewStates[expectedState].push(callback);
    if (!window.callbackMappedToRenewStates[expectedState]) {
      window.callbackMappedToRenewStates[expectedState] = (errorDesc, token, error) => {
        this.activeRenewals[resource] = null;

        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < window.callbackMappedToRenewStates[expectedState].length; ++i) {
          try {
            window.callbackMappedToRenewStates[expectedState][i](errorDesc, token, error);
          } catch (err) {
            this.warn(err);
          }
        }
        window.callbackMappedToRenewStates[expectedState] = null;
        window.callbackMappedToRenewStates[expectedState] = null;
      };
    }
  }

  // var errorResponse = {error:'', error_description:''};
  // var token = 'string token';
  // callback(errorResponse, token)
  // with callback
  /**
   * Acquires access token with hidden iframe
   * @ignore
   */
  private renewToken(resource, callback) {
    // use iframe to try refresh token
    // use given resource to create new authz url
    this.info('renewToken is called for resource:' + resource);
    const frameHandle = this.addAdalFrame('adalRenewFrame' + resource);
    const expectedState = guid() + '|' + resource;
    this.config.state = expectedState;
    // renew happens in iframe, so it keeps javascript context
    window.renewStates.push(expectedState);

    this.verbose('Renew token Expected state: ' + expectedState);
    let urlNavigate = this.getNavigateUrl('token', resource) + '&prompt=none';
    urlNavigate = this.addHintParameters(urlNavigate);

    this.registerCallback(expectedState, resource, callback);
    this.verbose('Navigate to:' + urlNavigate);
    frameHandle.setAttribute('src', 'about:blank');
    this.loadFrameTimeout(urlNavigate, 'adalRenewFrame' + resource, resource);
  }

  /**
   * Renews idtoken for app's own backend when resource is clientId and calls the callback with token/error
   * @ignore
   */
  private renewIdToken(callback) {
    // use iframe to try refresh token
    this.info('renewIdToken is called');
    const frameHandle = this.addAdalFrame('adalIdTokenFrame');
    const expectedState = guid() + '|' + this.config.clientId;
    this.idTokenNonce = guid();
    this.saveItem(this.CONSTANTS.STORAGE.NONCE_IDTOKEN, this.idTokenNonce);
    this.config.state = expectedState;
    // renew happens in iframe, so it keeps javascript context
    window.renewStates.push(expectedState);

    this.verbose('Renew Idtoken Expected state: ' + expectedState);
    let urlNavigate = this.getNavigateUrl('id_token', null) + '&prompt=none';
    urlNavigate = this.addHintParameters(urlNavigate);

    urlNavigate += '&nonce=' + encodeURIComponent(this.idTokenNonce);
    this.registerCallback(expectedState, this.config.clientId, callback);
    this.idTokenNonce = null;
    this.verbose('Navigate to:' + urlNavigate);
    frameHandle.setAttribute('src', 'about:blank');
    this.loadFrameTimeout(urlNavigate, 'adalIdTokenFrame', this.config.clientId);
  }

  /**
   * Checks if the authorization endpoint URL contains query string parameters
   * @ignore
   */
  private urlContainsQueryStringParameter(name, url) {
    // regex to detect pattern of a ? or & followed by the name parameter and an equals character
    const regex = new RegExp('[\\?&]' + name + '=');
    return regex.test(url);
  }

  // Calling _loadFrame but with a timeout to signal failure in loadframeStatus. Callbacks are left
  // registered when network errors occur and subsequent token requests for same resource are registered to the pending request
  /**
   * @ignore
   */
  private loadFrameTimeout(urlNavigation, frameName, resource) {
    // set iframe session to pending
    this.verbose('Set loading state to pending for: ' + resource);
    this.saveItem(this.CONSTANTS.STORAGE.RENEW_STATUS + resource, this.CONSTANTS.TOKEN_RENEW_STATUS_IN_PROGRESS);
    this.loadFrame(urlNavigation, frameName);
    setTimeout(() => {
      if (this.getItem(this.CONSTANTS.STORAGE.RENEW_STATUS + resource) === this.CONSTANTS.TOKEN_RENEW_STATUS_IN_PROGRESS) {
        // fail the iframe session if it's in pending state
        // eslint-disable-next-line max-len
        this.verbose('Loading frame has timed out after: ' + this.CONSTANTS.LOADFRAME_TIMEOUT / 1000 + ' seconds for resource ' + resource);
        const expectedState = this.activeRenewals[resource];
        if (expectedState && window.callbackMappedToRenewStates[expectedState]) {
          // eslint-disable-next-line max-len
          window.callbackMappedToRenewStates[expectedState][0]('Token renewal operation failed due to timeout', null, 'Token Renewal Failed');
        }

        this.saveItem(this.CONSTANTS.STORAGE.RENEW_STATUS + resource, this.CONSTANTS.TOKEN_RENEW_STATUS_CANCELED);
      }
    }, this.CONSTANTS.LOADFRAME_TIMEOUT);
  }

  /**
   * Loads iframe with authorization endpoint URL
   * @ignore
   */
  private loadFrame(urlNavigate, frameName) {
    // This trick overcomes iframe navigation in IE
    // IE does not load the page consistently in iframe
    this.info('LoadFrame: ' + frameName);
    const frameCheck = frameName;
    setTimeout(() => {
      const frameHandle = this.addAdalFrame(frameCheck);
      if (frameHandle.getAttribute('src') === '' || frameHandle.getAttribute('src') === 'about:blank') {
        frameHandle.setAttribute('src', urlNavigate);
        this.loadFrame(urlNavigate, frameCheck);
      }
    }, 500);
  }

  private isEmpty(str) {
    return typeof str === 'undefined' || !str || 0 === str.length;
  }

  /**
   * Adds login_hint to authorization URL which is used to pre-fill the
   *  username field of sign in page for the user if known ahead of time.
   * domain_hint can be one of users/organisations which when added skips the email based discovery process of the user.
   * @ignore
   */
  private addHintParameters(urlNavigate) {
    // include hint params only if upn is present
    if (this.user && this.profile && this.profile.hasOwnProperty('upn')) {
      // don't add login_hint twice if user provided it in the extraQueryParameter value
      if (!this.urlContainsQueryStringParameter('login_hint', urlNavigate)) {
        // add login_hint
        urlNavigate += '&login_hint=' + encodeURIComponent(this.profile.upn);
      }

      // don't add domain_hint twice if user provided it in the extraQueryParameter value
      if (!this.urlContainsQueryStringParameter('domain_hint', urlNavigate) && this.profile.upn.indexOf('@') > -1) {
        const parts = this.profile.upn.split('@');
        // local part can include @ in quotes. Sending last part handles that.
        urlNavigate += '&domain_hint=' + encodeURIComponent(parts[parts.length - 1]);
      }
    }

    return urlNavigate;
  }

  // Constructs the authorization endpoint URL and returns it.
  private getNavigateUrl(responseType, resource) {
    // eslint-disable-next-line max-len
    const urlNavigate = this.instance + this.config.localLoginUrl + this.serialize(responseType, this.config, resource) + this.addLibMetadata();

    this.info('Navigate url:' + urlNavigate);
    return urlNavigate;
  }

  // Serializes the parameters for the authorization endpoint URL and returns the serialized uri string.
  private serialize(responseType, obj, resource) {
    const str = [];
    if (obj !== null) {
      str.push('?response_type=' + responseType);
      str.push('client_id=' + encodeURIComponent(obj.clientId));
      if (resource) {
        str.push('resource=' + encodeURIComponent(resource));
        str.push('provider=' + encodeURIComponent(resource));
      }

      if (obj.tenantId) {
        str.push('tenantId=' + obj.tenantId);
      }

      str.push('opener=iframe');
      str.push('returnUrl=' + encodeURIComponent(obj.redirectUri));
      str.push('redirect_uri=' + encodeURIComponent(obj.redirectUri));
      str.push('state=' + encodeURIComponent(obj.state));

      if (obj.hasOwnProperty('slice')) {
        str.push('slice=' + encodeURIComponent(obj.slice));
      }

      if (obj.hasOwnProperty('extraQueryParameter')) {
        str.push(obj.extraQueryParameter);
      }

      const correlationId = obj.correlationId ? obj.correlationId : guid();
      str.push('client-request-id=' + encodeURIComponent(correlationId));
    }

    return str.join('&');
  }

  // Return the number of milliseconds since 1970/01/01
  private now() {
    return Math.round(new Date().getTime() / 1000.0);
  }

  // Adds the hidden iframe for silent token renewal
  private addAdalFrame(iframeId) {
    if (typeof iframeId === 'undefined') {
      return;
    }

    this.info('Add adal frame to document:' + iframeId);
    let adalFrame = document.getElementById(iframeId);

    if (!adalFrame) {
      // eslint-disable-next-line deprecation/deprecation
      if (document.createElement && document.documentElement && window.navigator.userAgent.indexOf('MSIE 5.0') === -1) {
        const ifr = document.createElement('iframe');
        ifr.setAttribute('id', iframeId);
        ifr.setAttribute('aria-hidden', 'true');
        ifr.style.visibility = 'hidden';
        ifr.style.position = 'absolute';
        ifr.style.width = ifr.style.height = ifr.style.borderWidth = '0px';

        adalFrame = document.getElementsByTagName('body')[0].appendChild(ifr);
      } else if (document.body && document.body.insertAdjacentHTML) {
        // eslint-disable-next-line max-len, @microsoft/sdl/no-inner-html
        document.body.insertAdjacentHTML('beforeend', '<iframe name="' + iframeId + '" id="' + iframeId + '" style="display:none"></iframe>');
      }
      // if (window.frames && window.frames[iframeId]) {
      //     adalFrame = window.frames[iframeId];
      // }
    }

    return adalFrame;
  }

  // Saves the key-value pair in the cache
  private saveItem(key, obj) {
    if (this.config && this.config.cacheLocation && this.config.cacheLocation === 'localStorage') {
      if (!this.supportsLocalStorage()) {
        this.info('Local storage is not supported');
        return false;
      }

      localStorage.setItem(key, obj);

      return true;
    }

    // Default as session storage
    if (!this.supportsSessionStorage()) {
      this.info('Session storage is not supported');
      return false;
    }

    sessionStorage.setItem(key, obj);
    return true;
  }

  // Searches the value for the given key in the cache
  private getItem(key) {
    if (this.config && this.config.cacheLocation && this.config.cacheLocation === 'localStorage') {
      if (!this.supportsLocalStorage()) {
        this.info('Local storage is not supported');
        return null;
      }

      return localStorage.getItem(key);
    }

    // Default as session storage
    if (!this.supportsSessionStorage()) {
      this.info('Session storage is not supported');
      return null;
    }

    return sessionStorage.getItem(key);
  }

  // Returns true if browser supports localStorage, false otherwise.
  private supportsLocalStorage() {
    try {
      if (!window.localStorage) {
        return false; // Test availability
      }
      window.localStorage.setItem('storageTest', 'A'); // Try write
      if (window.localStorage.getItem('storageTest') !== 'A') {
        return false;
      } // Test read/write
      window.localStorage.removeItem('storageTest'); // Try delete
      if (window.localStorage.getItem('storageTest')) {
        return false;
      } // Test delete
      return true; // Success
    } catch (e) {
      return false;
    }
  }

  // Returns true if browser supports sessionStorage, false otherwise.
  private supportsSessionStorage() {
    try {
      if (!window.sessionStorage) {
        return false;
      } // Test availability
      window.sessionStorage.setItem('storageTest', 'A'); // Try write
      if (window.sessionStorage.getItem('storageTest') !== 'A') {
        return false;
      } // Test read/write
      window.sessionStorage.removeItem('storageTest'); // Try delete
      if (window.sessionStorage.getItem('storageTest')) {
        return false;
      } // Test delete
      return true; // Success
    } catch (e) {
      return false;
    }
  }

  // Returns a cloned copy of the passed object.
  private cloneConfig(obj) {
    if (null === obj || 'object' !== typeof obj) {
      return obj;
    }

    const copy = {};
    for (const attr of Object.keys(obj)) {
      if (obj.hasOwnProperty(attr)) {
        copy[attr] = obj[attr];
      }
    }
    return copy;
  }

  // Adds the library version and returns it.
  private addLibMetadata() {
    // x-client-SKU
    // x-client-Ver
    return '&x-client-SKU=Js&x-client-Ver=' + this.libVersion();
  }

  // Checks the Logging Level, constructs the Log message and logs it. Users need to implement/override this method to turn on Logging.
  private log(level: number, message: string, error) {
    if (level <= 5) {
      const timestamp = new Date().toUTCString();
      let formattedMessage = '';

      if (this.config.correlationId) {
        // eslint-disable-next-line max-len
        formattedMessage =
          timestamp + ':' + this.config.correlationId + '-' + this.libVersion() + '-' + this.CONSTANTS.LEVEL_STRING_MAP[level] + ' ' + message;
      } else {
        formattedMessage = timestamp + ':' + this.libVersion() + '-' + this.CONSTANTS.LEVEL_STRING_MAP[level] + ' ' + message;
      }

      if (error) {
        formattedMessage += '\nstack:\n' + error.stack;
      }

      // eslint-disable-next-line no-console
      console.log(formattedMessage);
    }
  }

  // Logs messages when Logging Level is set to 1.
  private warn(message: string) {
    this.log(this.CONSTANTS.LOGGING_LEVEL.WARN, message, null);
  }

  // Logs messages when Logging Level is set to 2.
  private info(message: string) {
    this.log(this.CONSTANTS.LOGGING_LEVEL.INFO, message, null);
  }

  // Logs messages when Logging Level is set to 3.
  private verbose(message: string) {
    this.log(this.CONSTANTS.LOGGING_LEVEL.VERBOSE, message, null);
  }

  // Returns the library version.
  private libVersion() {
    return '1.0.15';
  }
}
